import React, { useState, useEffect, useRef } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';
import { CheckinParticipant, PlayCheckinSound, formatMexicoDate } from './../../../../utilities/BL/QuizzB9Zone';

export function QuizzB9ZoneCheckinModal({ isOpen, onClose, onCheckinSuccess, totalWinners = 50, initialAttended = 0 }) {
    const { showMessage } = useFloatMessage();

    // Scan & Process States
    const [manualInput, setManualInput] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [isMuted, setIsMuted] = useState(false);
    const [attendedCount, setAttendedCount] = useState(initialAttended);

    // Latest Result State
    const [scanResult, setScanResult] = useState(null); // { status, message, name, email, phone, passCode, winnerRank, attendedAtUtc, disqualifiedReason }

    // Session History List
    const [sessionHistory, setSessionHistory] = useState([]);

    // Camera WebRTC States
    const [isCameraActive, setIsCameraActive] = useState(false);
    const [cameraFacingMode, setCameraFacingMode] = useState('environment'); // 'environment' or 'user'
    const videoRef = useRef(null);
    const streamRef = useRef(null);
    const animationFrameIdRef = useRef(null);

    // Keyboard Wedge Scanner Buffers
    const scanBufferRef = useRef('');
    const lastKeyTimeRef = useRef(0);
    const isManualFocusedRef = useRef(false);

    useEffect(() => {
        setAttendedCount(initialAttended);
    }, [initialAttended]);

    // Handle Hardware Barcode / QR Scanner (Keyboard Wedge)
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (isManualFocusedRef.current) return;

            const now = Date.now();
            const timeDiff = now - lastKeyTimeRef.current;
            lastKeyTimeRef.current = now;

            if (e.key === 'Enter') {
                if (scanBufferRef.current.length >= 3) {
                    const scanned = scanBufferRef.current;
                    scanBufferRef.current = '';
                    processCheckin(scanned);
                } else {
                    scanBufferRef.current = '';
                }
                return;
            }

            // Normal typing delay vs fast hardware scanner (< 60ms)
            if (timeDiff > 120 && scanBufferRef.current.length > 0) {
                scanBufferRef.current = '';
            }

            if (e.key.length === 1) {
                scanBufferRef.current += e.key;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    // Camera WebRTC Stream Lifecycle
    useEffect(() => {
        if (isCameraActive && isOpen) {
            startCamera();
        } else {
            stopCamera();
        }

        return () => {
            stopCamera();
        };
    }, [isCameraActive, cameraFacingMode, isOpen]);

    const startCamera = async () => {
        stopCamera();
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: 'Tu navegador o dispositivo no soporta acceso directo a cámara WebRTC.'
                });
                setIsCameraActive(false);
                return;
            }

            const constraints = {
                video: {
                    facingMode: cameraFacingMode,
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                },
                audio: false
            };

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;

            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                videoRef.current.setAttribute('playsinline', 'true');
                await videoRef.current.play();
                startQrDetectionLoop();
            }
        } catch (error) {
            console.error("Error al iniciar cámara:", error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'No se pudo acceder a la cámara. Revisa los permisos del navegador.'
            });
            setIsCameraActive(false);
        }
    };

    const stopCamera = () => {
        if (animationFrameIdRef.current) {
            cancelAnimationFrame(animationFrameIdRef.current);
            animationFrameIdRef.current = null;
        }

        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
    };

    // Native BarcodeDetector (Supported in modern Chrome/Android/Edge) or canvas scan
    const startQrDetectionLoop = () => {
        if ('BarcodeDetector' in window) {
            const barcodeDetector = new window.BarcodeDetector({ formats: ['qr_code', 'code_128', 'code_39', 'data_matrix'] });

            const detect = async () => {
                if (videoRef.current && videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA) {
                    try {
                        const barcodes = await barcodeDetector.detect(videoRef.current);
                        if (barcodes && barcodes.length > 0) {
                            const rawVal = barcodes[0].rawValue;
                            if (rawVal) {
                                processCheckin(rawVal);
                                // Pause briefly to prevent rapid duplicate scans
                                await new Promise(r => setTimeout(r, 2000));
                            }
                        }
                    } catch (e) {
                        // Ignore detection frame drops
                    }
                }
                if (isCameraActive) {
                    animationFrameIdRef.current = requestAnimationFrame(detect);
                }
            };

            animationFrameIdRef.current = requestAnimationFrame(detect);
        }
    };

    const processCheckin = async (scanData) => {
        if (!scanData || isProcessing) return;
        setIsProcessing(true);

        try {
            const res = await CheckinParticipant(scanData);

            if (res.success && res.status === 'ACCESS_GRANTED') {
                if (!isMuted) PlayCheckinSound('success');
                setAttendedCount(res.totalAttended || (attendedCount + 1));
                setScanResult({
                    status: 'ACCESS_GRANTED',
                    message: res.message || '¡Acceso Concedido! Pase verificado para ingresar a cancha.',
                    name: res.name,
                    email: res.email,
                    phone: res.phone,
                    passCode: res.passCode,
                    winnerRank: res.winnerRank,
                    attendedAtUtc: res.attendedAtUtc || new Date().toISOString()
                });

                // Add to session history
                setSessionHistory(prev => [{
                    id: Date.now(),
                    name: res.name,
                    passCode: res.passCode,
                    winnerRank: res.winnerRank,
                    time: new Date().toLocaleTimeString('es-MX', { hour12: false })
                }, ...prev.slice(0, 19)]);

                if (onCheckinSuccess) onCheckinSuccess(res);
            } else if (res.status === 'ALREADY_ATTENDED') {
                if (!isMuted) PlayCheckinSound('warning');
                setScanResult({
                    status: 'ALREADY_ATTENDED',
                    message: res.message || '⚠️ Pase ya utilizado previamente.',
                    name: res.name,
                    email: res.email,
                    phone: res.phone,
                    passCode: res.passCode,
                    winnerRank: res.winnerRank,
                    attendedAtUtc: res.attendedAtUtc
                });
            } else if (res.status === 'DISQUALIFIED') {
                if (!isMuted) PlayCheckinSound('error');
                setScanResult({
                    status: 'DISQUALIFIED',
                    message: res.message || '❌ Acceso Denegado. Participante descalificado.',
                    name: res.name,
                    email: res.email,
                    phone: res.phone,
                    passCode: res.passCode,
                    winnerRank: res.winnerRank,
                    disqualifiedReason: res.disqualifiedReason
                });
            } else {
                if (!isMuted) PlayCheckinSound('error');
                setScanResult({
                    status: 'INVALID',
                    message: res.message || 'No se encontró un pase de convivencia válido.',
                    name: res.name || null,
                    passCode: res.passCode || null
                });
            }
        } catch (error) {
            console.error("Error en proceso de checkin:", error);
            if (!isMuted) PlayCheckinSound('error');
            setScanResult({
                status: 'ERROR',
                message: 'Error de conexión con el servidor al validar el pase.'
            });
        } finally {
            setIsProcessing(false);
            setManualInput('');
        }
    };

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualInput.trim()) {
            processCheckin(manualInput.trim());
        }
    };

    const handleToggleCameraFacing = () => {
        setCameraFacingMode(prev => (prev === 'environment' ? 'user' : 'environment'));
    };

    if (!isOpen) return null;

    return (
        <div 
            className="modal fade show d-block" 
            tabIndex="-1" 
            style={{ backgroundColor: 'rgba(5, 10, 20, 0.88)', backdropFilter: 'blur(8px)', zIndex: 1080 }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0 rounded-4 bg-dark text-white">
                    {/* Header */}
                    <div className="modal-header border-bottom border-secondary px-4 py-3 bg-black">
                        <div className="d-flex align-items-center gap-3 flex-wrap">
                            <h5 className="modal-title fw-bold mb-0 d-flex align-items-center gap-2 text-warning">
                                <i className="bi bi-qr-code-scan"></i>
                                Control de Acceso a Cancha (Túnel 2)
                            </h5>
                            <span className="badge bg-danger px-3 py-2 fs-6 fw-bold">
                                Convivencia en la zona
                            </span>
                            <span className="badge bg-success-subtle text-success border border-success px-3 py-2 fs-6 fw-bold">
                                Ingresados: {attendedCount} / {totalWinners}
                            </span>
                        </div>

                        <div className="d-flex align-items-center gap-2 ms-auto">
                            <button 
                                type="button" 
                                className={`btn btn-sm ${isMuted ? 'btn-outline-secondary' : 'btn-outline-warning'}`}
                                onClick={() => setIsMuted(!isMuted)}
                                title={isMuted ? "Activar sonido" : "Silenciar"}
                            >
                                <i className={`bi ${isMuted ? 'bi-volume-mute-fill' : 'bi-volume-up-fill'}`}></i>
                            </button>
                            <button 
                                type="button" 
                                className="btn-close btn-close-white" 
                                aria-label="Close"
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>

                    <div className="modal-body p-4 bg-dark">
                        <div className="row g-4">
                            {/* Columna Izquierda: Escaneo y Controles */}
                            <div className="col-12 col-lg-5">
                                {/* Entrada Manual / Búsqueda */}
                                <div className="card border-secondary bg-black p-3 rounded-3 shadow-sm mb-3">
                                    <label className="form-label small fw-bold text-secondary mb-2">
                                        <i className="bi bi-upc-scan me-1 text-warning"></i>
                                        Pistola USB / Bluetooth o Entrada Manual
                                    </label>
                                    <form onSubmit={handleManualSubmit}>
                                        <div className="input-group">
                                            <input 
                                                type="text" 
                                                className="form-control bg-dark text-white border-secondary font-monospace"
                                                placeholder="Escanea o escribe folio (#FIEXARA01)..."
                                                value={manualInput}
                                                onChange={(e) => setManualInput(e.target.value)}
                                                onFocus={() => { isManualFocusedRef.current = true; }}
                                                onBlur={() => { isManualFocusedRef.current = false; }}
                                                disabled={isProcessing}
                                                autoFocus
                                            />
                                            <button 
                                                type="submit" 
                                                className="btn btn-danger fw-bold"
                                                disabled={isProcessing || !manualInput.trim()}
                                            >
                                                {isProcessing ? (
                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                ) : (
                                                    <i className="bi bi-arrow-return-left"></i>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                    <small className="text-muted mt-2 d-block" style={{ fontSize: '0.72rem' }}>
                                        💡 La pistola lectora USB/Bluetooth funciona de forma continua automáticamente.
                                    </small>
                                </div>

                                {/* Escáner por Cámara */}
                                <div className="card border-secondary bg-black p-3 rounded-3 shadow-sm mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="form-label small fw-bold text-secondary mb-0">
                                            <i className="bi bi-camera-video me-1 text-info"></i>
                                            Escáner por Cámara Móvil / Web
                                        </label>
                                        <button 
                                            type="button" 
                                            className={`btn btn-sm ${isCameraActive ? 'btn-danger' : 'btn-outline-info'}`}
                                            onClick={() => setIsCameraActive(!isCameraActive)}
                                        >
                                            <i className={`bi ${isCameraActive ? 'bi-stop-fill' : 'bi-camera-fill'} me-1`}></i>
                                            {isCameraActive ? 'Apagar Cámara' : 'Activar Cámara'}
                                        </button>
                                    </div>

                                    {isCameraActive ? (
                                        <div className="position-relative rounded overflow-hidden border border-info" style={{ minHeight: '220px', background: '#000' }}>
                                            <video 
                                                ref={videoRef} 
                                                className="w-100 h-100" 
                                                style={{ objectFit: 'cover', maxHeight: '240px' }}
                                            />
                                            {/* Retícula de escaneo */}
                                            <div 
                                                className="position-absolute top-50 start-50 translate-middle border border-2 border-warning rounded-3"
                                                style={{ width: '160px', height: '160px', boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)' }}
                                            ></div>
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-dark position-absolute bottom-0 end-0 m-2 opacity-75"
                                                onClick={handleToggleCameraFacing}
                                                title="Cambiar cámara"
                                            >
                                                <i className="bi bi-arrow-repeat"></i>
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="p-3 text-center text-muted bg-dark rounded border border-secondary border-opacity-50 small">
                                            <i className="bi bi-camera-video-off fs-3 d-block mb-1"></i>
                                            Cámara apagada. Actívala si deseas escanear desde este dispositivo.
                                        </div>
                                    )}
                                </div>

                                {/* Historial de la Sesión */}
                                <div className="card border-secondary bg-black p-3 rounded-3 shadow-sm">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="small fw-bold text-secondary">Últimos Ingresos de la Sesión:</span>
                                        <span className="badge bg-secondary">{sessionHistory.length}</span>
                                    </div>
                                    {sessionHistory.length > 0 ? (
                                        <div className="d-flex flex-column gap-1 overflow-auto" style={{ maxHeight: '160px' }}>
                                            {sessionHistory.map((item) => (
                                                <div key={item.id} className="d-flex justify-content-between align-items-center bg-dark p-2 rounded small border border-secondary border-opacity-25">
                                                    <div>
                                                        <span className="fw-bold text-white me-2">{item.name}</span>
                                                        <span className="badge bg-warning text-dark font-monospace" style={{ fontSize: '0.65rem' }}>
                                                            {item.passCode}
                                                        </span>
                                                    </div>
                                                    <span className="text-success fw-bold font-monospace small">
                                                        {item.time}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-muted small text-center mb-0 py-2">
                                            Aún no hay ingresos en esta sesión.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Columna Derecha: Tarjeta de Feedback Visual Grande */}
                            <div className="col-12 col-lg-7">
                                {isProcessing ? (
                                    <div className="card border-0 bg-black text-center p-5 rounded-4 shadow-lg h-100 d-flex justify-content-center align-items-center">
                                        <div className="spinner-grow text-warning mb-3" style={{ width: '4rem', height: '4rem' }} role="status"></div>
                                        <h4 className="fw-bold text-warning">Verificando firma y folio...</h4>
                                        <p className="text-secondary small">Consultando base de datos segura de ADSL</p>
                                    </div>
                                ) : scanResult?.status === 'ACCESS_GRANTED' ? (
                                    <div className="card border-3 border-success bg-success bg-opacity-10 text-center p-4 rounded-4 shadow-lg h-100 d-flex flex-column justify-content-center align-items-center">
                                        <div className="bg-success text-white rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center" style={{ width: '90px', height: '90px' }}>
                                            <i className="bi bi-check-lg" style={{ fontSize: '3.5rem' }}></i>
                                        </div>
                                        <span className="badge bg-success text-white fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ✅ ACCESO CONCEDIDO
                                        </span>
                                        <h2 className="fw-bold text-white text-uppercase mb-1 mt-2">
                                            {scanResult.name}
                                        </h2>
                                        <div className="d-flex align-items-center justify-content-center gap-2 flex-wrap mb-3">
                                            <span className="badge bg-warning text-dark fs-5 font-monospace px-3 py-1 fw-bold">
                                                {scanResult.passCode}
                                            </span>
                                            <span className="badge bg-dark text-warning border border-warning fs-6 px-3 py-1 fw-bold">
                                                🏆 GANADOR #{scanResult.winnerRank}
                                            </span>
                                        </div>
                                        <p className="text-success-emphasis fw-semibold mb-2 fs-6">
                                            Pase individual auténtico verificado. Autorizado a pasar a cancha.
                                        </p>
                                        <div className="text-muted small">
                                            <i className="bi bi-clock me-1"></i> Registrado: {formatMexicoDate(scanResult.attendedAtUtc)}
                                        </div>
                                    </div>
                                ) : scanResult?.status === 'ALREADY_ATTENDED' ? (
                                    <div className="card border-3 border-warning bg-warning bg-opacity-10 text-center p-4 rounded-4 shadow-lg h-100 d-flex flex-column justify-content-center align-items-center">
                                        <div className="bg-warning text-dark rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center" style={{ width: '90px', height: '90px' }}>
                                            <i className="bi bi-exclamation-triangle-fill" style={{ fontSize: '3.5rem' }}></i>
                                        </div>
                                        <span className="badge bg-warning text-dark fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ⚠️ PASE YA UTILIZADO
                                        </span>
                                        <h3 className="fw-bold text-white text-uppercase mb-1 mt-2">
                                            {scanResult.name}
                                        </h3>
                                        <span className="badge bg-dark text-warning fs-5 font-monospace px-3 py-1 fw-bold mb-3">
                                            {scanResult.passCode}
                                        </span>
                                        <div className="alert alert-warning border-0 bg-warning text-dark fw-bold px-4 py-2 rounded-3 mb-2">
                                            Este pase ya ingresó previamente a las: {formatMexicoDate(scanResult.attendedAtUtc)}
                                        </div>
                                        <p className="text-white-50 small mb-0">
                                            No se permite el acceso repetido con el mismo código QR o captura de pantalla.
                                        </p>
                                    </div>
                                ) : scanResult?.status === 'DISQUALIFIED' ? (
                                    <div className="card border-3 border-danger bg-danger bg-opacity-10 text-center p-4 rounded-4 shadow-lg h-100 d-flex flex-column justify-content-center align-items-center">
                                        <div className="bg-danger text-white rounded-circle p-3 mb-3 d-inline-flex justify-content-center align-items-center" style={{ width: '90px', height: '90px' }}>
                                            <i className="bi bi-shield-x" style={{ fontSize: '3.5rem' }}></i>
                                        </div>
                                        <span className="badge bg-danger text-white fs-5 px-4 py-2 rounded-pill fw-bold mb-2">
                                            ❌ ACCESO DENEGADO
                                        </span>
                                        <h3 className="fw-bold text-white text-uppercase mb-1 mt-2">
                                            {scanResult.name}
                                        </h3>
                                        <span className="badge bg-dark text-danger fs-5 font-monospace px-3 py-1 fw-bold mb-3">
                                            {scanResult.passCode}
                                        </span>
                                        <div className="alert alert-danger border-0 bg-danger text-white fw-bold px-4 py-2 rounded-3 mb-2">
                                            Participante descalificado: {scanResult.disqualifiedReason || 'Sin motivo registrado'}
                                        </div>
                                        <p className="text-white-50 small mb-0">
                                            Pase cancelado en el sistema por el administrador.
                                        </p>
                                    </div>
                                ) : scanResult?.status === 'INVALID' || scanResult?.status === 'ERROR' ? (
                                    <div className="card border-3 border-secondary bg-black text-center p-4 rounded-4 shadow-lg h-100 d-flex flex-column justify-content-center align-items-center">
                                        <i className="bi bi-x-circle text-danger mb-3" style={{ fontSize: '4rem' }}></i>
                                        <h4 className="fw-bold text-danger mb-2">Pase No Válido</h4>
                                        <p className="text-white fs-6 mb-3">{scanResult.message}</p>
                                        <span className="text-muted small">Verifica que el aficionado muestre su tarjeta oficial de Convivencia en la zona.</span>
                                    </div>
                                ) : (
                                    /* Estado Inicial en Espera */
                                    <div className="card border-2 border-dashed border-secondary bg-black text-center p-5 rounded-4 shadow-lg h-100 d-flex flex-column justify-content-center align-items-center">
                                        <i className="bi bi-qr-code text-warning mb-3" style={{ fontSize: '4.5rem', opacity: 0.8 }}></i>
                                        <h3 className="fw-bold text-white mb-2">Listo para Escanear</h3>
                                        <p className="text-secondary fs-6 mb-3">
                                            Apunta la pistola lectora o la cámara al código QR de la tarjeta dorada del aficionado.
                                        </p>
                                        <div className="d-flex align-items-center gap-2 text-warning small bg-dark px-3 py-2 rounded-pill border border-warning border-opacity-25">
                                            <i className="bi bi-broadcast"></i>
                                            <span>Esperando lectura óptica o manual...</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="modal-footer border-top border-secondary px-4 py-3 bg-black d-flex justify-content-between">
                        <button 
                            type="button" 
                            className="btn btn-outline-secondary px-4 fw-semibold"
                            onClick={onClose}
                        >
                            Cerrar Módulo
                        </button>

                        <div className="text-muted small">
                            Personal de Seguridad — Estadio El Encanto
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuizzB9ZoneCheckinModal;
